'use client';

import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useVideoLazyLoad } from '@/hooks/useVideoLazyLoad';
import { Volume2, VolumeX, Sparkles, Home, ShieldCheck } from 'lucide-react';

export default function ExperienceHopo() {
    const [isMuted, setIsMuted] = useState(true);
    const videoRef = useRef<HTMLVideoElement>(null);
    useVideoLazyLoad(videoRef);

    const toggleAudio = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted;
            setIsMuted(videoRef.current.muted);
        }
    };

    return (
        <section className="py-12 md:py-20 bg-background dark:bg-neutral-950 px-4 md:px-6">
            <div className="max-w-6xl mx-auto space-y-10 md:space-y-16">
                {/* Simplified Header */}
                <div className="flex flex-col items-center text-center space-y-4 md:space-y-6">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        className="inline-flex items-center gap-1.5 md:gap-2 px-3 py-1 md:px-4 md:py-1.5 rounded-full bg-primary/10 text-primary text-[10px] md:text-xs font-bold tracking-[0.2em] uppercase"
                    >
                        <Sparkles size={14} className="w-3.5 h-3.5 md:w-3.5 md:h-3.5" />
                        Resident Experience
                    </motion.div>

                    <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1, duration: 0.8 }}
                        className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-medium text-foreground leading-[1.2] md:leading-[1.1] tracking-tight max-w-4xl px-2"
                    >
                        Living <span className="font-serif italic text-primary">Reimagined</span> for the <br className="hidden md:block" /> Modern Resident
                    </motion.h2>
                </div>

                {/* Full Width Video */}
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2, duration: 1 }}
                    className="relative aspect-video md:aspect-[2/1] lg:aspect-[20/9] w-full rounded-[2.5rem] md:rounded-[4rem] overflow-hidden shadow-[0_32px_64px_-16px_rgba(0,0,0,0.2)] bg-black group"
                >
                    <video
                        ref={videoRef}
                        autoPlay
                        loop
                        muted={isMuted}
                        title="Life at Hopo"
                        playsInline
                        className="w-full h-full object-cover opacity-90 transition-opacity hover:opacity-100 duration-500 scale-[1.01]"
                    >
                        <source src="/video/tenants.mp4" type="video/mp4" />
                    </video>

                    {/* Gradient Overlays */}
                    <div className="absolute inset-x-0 bottom-0 h-1/2 md:h-1/3 bg-gradient-to-t from-black/80 md:from-black/60 to-transparent pointer-events-none" />
                    <div className="absolute inset-x-0 top-0 h-1/4 bg-gradient-to-b from-black/40 md:from-black/20 to-transparent pointer-events-none" />

                    {/* Audio Control */}
                    <button
                        onClick={toggleAudio}
                        aria-label={isMuted ? "Unmute audio" : "Mute audio"}
                        className="absolute bottom-4 left-4 sm:bottom-8 sm:left-8 md:bottom-12 md:left-12 z-20 w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full bg-white/10 backdrop-blur-2xl border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all active:scale-95 shadow-2xl group/btn"
                    >
                        {isMuted ? (
                            <VolumeX className="w-5 h-5 sm:w-6 sm:h-6 group-hover/btn:scale-110 transition-transform" />
                        ) : (
                            <Volume2 className="w-5 h-5 sm:w-6 sm:h-6 group-hover/btn:scale-110 transition-transform" />
                        )}
                    </button>

                    <div className="absolute bottom-4 right-4 sm:bottom-8 sm:right-8 md:bottom-12 md:right-12">
                        <div className="flex items-center gap-2 sm:gap-3 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-black/20 backdrop-blur-md border border-white/10">
                            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-primary animate-pulse" />
                            <span className="text-white text-[8px] sm:text-[10px] md:text-xs font-bold uppercase tracking-[0.2em] whitespace-nowrap">POV: Life at Hopo</span>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
